import { onSharedFolderFetchFailed } from '../actions/onSharedFolderFetchFailed';
import findSharedFolders from '../services/findSharedFolders';
import { logUsage } from 'owa-analytics';
import { getAggregationBucket } from 'owa-analytics-actions';
import { getSharedFolders } from 'owa-folders/lib/actions/getSharedFolders';
import {
    getFolderTreeLoadingState,
    getFolderTreeCurrentLoadedIndex,
    FolderTreeLoadStateEnum,
    onFindFolderSuccess,
    setLoadingStateForFolderTree,
    getSharedFolderRoot,
} from 'owa-folders';
import {
    SHARED_FOLDERS_TREE_TYPE,
    SHARED_FOLDER_ROOT_DISTINGUISHED_ID,
} from 'owa-folders-constants';
import distinguishedFolderId from 'owa-service/lib/factory/distinguishedFolderId';
import { trace } from 'owa-trace';
import { orchestrator } from 'satcheljs';

const INBOX_FOLDER_NAME = 'inbox';

/**
 * An orchestrator that gets the shared folders of a particular user
 */
export default orchestrator(getSharedFolders, async actionMessage => {
    const {
        userIdentity,
        sharedUserMailboxSmtpAddress,
        actionSource,
        isLoadingMore,
    } = actionMessage;

    // if loading is in progress or it is loaded and we are not loading more, just return
    const loadingState = getFolderTreeLoadingState(sharedUserMailboxSmtpAddress, userIdentity);
    if (
        loadingState === FolderTreeLoadStateEnum.Loading ||
        (loadingState === FolderTreeLoadStateEnum.Loaded && !isLoadingMore)
    ) {
        return;
    }
    // sets the loading state to 'Loading' for corresponding shared folder tree
    setLoadingStateForFolderTree(
        sharedUserMailboxSmtpAddress,
        FolderTreeLoadStateEnum.Loading,
        userIdentity
    );
    let findFolderResponse;
    try {
        findFolderResponse = await findSharedFolders(
            [
                distinguishedFolderId({ Id: SHARED_FOLDER_ROOT_DISTINGUISHED_ID }),
                distinguishedFolderId({ Id: INBOX_FOLDER_NAME }),
            ],
            sharedUserMailboxSmtpAddress,
            getFolderTreeCurrentLoadedIndex(sharedUserMailboxSmtpAddress, userIdentity),
            userIdentity
        );
    } catch (error) {
        // The error dialog to be shown to user is handled below in the onSharedFolderFetchFailed action
        trace.error('FindFolder API returned error ' + error);
        setLoadingStateForFolderTree(
            sharedUserMailboxSmtpAddress,
            FolderTreeLoadStateEnum.ErrorLoading,
            userIdentity
        );
        return;
    }

    if (
        !findFolderResponse ||
        !findFolderResponse.RootFolder ||
        !findFolderResponse.RootFolder.Folders ||
        !findFolderResponse.RootFolder.ParentFolder
    ) {
        onSharedFolderFetchFailed(sharedUserMailboxSmtpAddress, actionSource);

        setLoadingStateForFolderTree(
            sharedUserMailboxSmtpAddress,
            FolderTreeLoadStateEnum.ErrorLoading,
            userIdentity
        );
        return;
    }

    const rootDisplayName = getSharedFolderRoot(sharedUserMailboxSmtpAddress).DisplayName;

    onFindFolderSuccess(
        findFolderResponse.RootFolder,
        SHARED_FOLDER_ROOT_DISTINGUISHED_ID,
        SHARED_FOLDERS_TREE_TYPE,
        sharedUserMailboxSmtpAddress,
        rootDisplayName,
        sharedUserMailboxSmtpAddress,
        userIdentity
    );
    logSharedFolderCountPerUser(findFolderResponse.RootFolder.TotalItemsInView);
});

/**
 * Log the Shared Folder count shared by a user with the current user.
 * @param count - number of folder shared by a user with the current user.
 */
function logSharedFolderCountPerUser(count: number) {
    logUsage('Triage_SharedFolders_OpenFolder', [getAggregationBucket({ value: count })]);
}
